import { logger } from '@/utils/productionLogger';
import { performanceMonitor } from '@/services/performanceMonitorService';

/**
 * 🚀 AUGMENT V85.0 - Enhanced Cache-First Architecture
 * Multi-layer caching with intelligent invalidation and performance optimization
 */

interface CacheEntry<T> {
  data: T;
  timestamp: number;
  expiry: number;
  userId: string;
  version: number;
  accessCount: number;
  lastAccessed: number;
  priority: 'critical' | 'high' | 'medium' | 'low';
}

interface CacheStats {
  hits: number;
  misses: number;
  evictions: number;
  totalSize: number;
  entryCount: number;
}

export class EnhancedChatCache {
  private static readonly VERSION = 2;
  private static readonly MAX_MEMORY_ENTRIES = 100;
  private static readonly MAX_STORAGE_SIZE = 100 * 1024 * 1024; // 100MB
  private static readonly DEFAULT_TTL = 5 * 60 * 1000; // 5 minutes
  private static readonly CRITICAL_TTL = 30 * 60 * 1000; // 30 minutes
  
  // Multi-layer cache architecture
  private static memoryCache = new Map<string, CacheEntry<any>>();
  private static stats: CacheStats = {
    hits: 0,
    misses: 0,
    evictions: 0,
    totalSize: 0,
    entryCount: 0
  };

  /**
   * 🚀 CACHE-FIRST: Get data with intelligent fallback strategy
   */
  static get<T>(
    key: string, 
    userId: string, 
    options: { 
      fallbackToStorage?: boolean;
      updateAccessStats?: boolean;
    } = {}
  ): T | null {
    const startTime = performance.now();
    const { fallbackToStorage = true, updateAccessStats = true } = options;

    try {
      // Layer 1: Memory cache (fastest)
      const memoryEntry = this.memoryCache.get(this.getMemoryKey(key, userId));
      if (memoryEntry && this.isValid(memoryEntry)) {
        if (updateAccessStats) {
          memoryEntry.accessCount++;
          memoryEntry.lastAccessed = Date.now();
        }
        
        this.stats.hits++;
        const duration = performance.now() - startTime;
        performanceMonitor.recordQuery('enhanced_cache_memory_hit', duration, { cached: true });
        
        logger.debug(`🚀 CACHE HIT (Memory): ${key} for user ${userId} (${duration.toFixed(2)}ms)`);
        // V323-DEBUG: Log cache return for chat_init_ to trace stale cache
        if (key.startsWith('chat_init_')) {
          const d = memoryEntry.data as { id?: string; user1_id?: string; user2_id?: string };
          console.log('🔍 V323-DEBUG Step 2: EnhancedChatCache HIT for key =', key, '| returned chat id =', d?.id, '| user1_id =', d?.user1_id, '| user2_id =', d?.user2_id);
        }
        return memoryEntry.data;
      }

      // Layer 2: localStorage cache (fallback)
      if (fallbackToStorage) {
        const storageData = this.getFromStorage<T>(key, userId);
        if (storageData) {
          // Promote to memory cache for faster future access
          this.setMemoryCache(key, userId, storageData, { priority: 'medium' });
          
          this.stats.hits++;
          const duration = performance.now() - startTime;
          performanceMonitor.recordQuery('enhanced_cache_storage_hit', duration, { cached: true });
          
          logger.debug(`🚀 CACHE HIT (Storage): ${key} for user ${userId} (${duration.toFixed(2)}ms)`);
          // V323-DEBUG: Log cache return for chat_init_ to trace stale cache
          if (key.startsWith('chat_init_')) {
            const d = storageData as { id?: string; user1_id?: string; user2_id?: string };
            console.log('🔍 V323-DEBUG Step 2: EnhancedChatCache HIT (Storage) for key =', key, '| returned chat id =', d?.id, '| user1_id =', d?.user1_id, '| user2_id =', d?.user2_id);
          }
          return storageData;
        }
      }

      // Cache miss
      this.stats.misses++;
      const duration = performance.now() - startTime;
      performanceMonitor.recordQuery('enhanced_cache_miss', duration, { cached: false });
      
      logger.debug(`❌ CACHE MISS: ${key} for user ${userId} (${duration.toFixed(2)}ms)`);
      return null;

    } catch (error) {
      const duration = performance.now() - startTime;
      performanceMonitor.recordQuery('enhanced_cache_error', duration, { 
        cached: false, 
        error: error instanceof Error ? error.message : String(error) 
      });
      
      logger.error(`Cache get error for ${key}:`, error);
      return null;
    }
  }

  /**
   * 🚀 INTELLIGENT SET: Store data with smart eviction and priority
   */
  static set<T>(
    key: string, 
    userId: string, 
    data: T, 
    options: {
      ttl?: number;
      priority?: 'critical' | 'high' | 'medium' | 'low';
      persistToStorage?: boolean;
    } = {}
  ): boolean {
    const startTime = performance.now();
    const { 
      ttl = this.DEFAULT_TTL, 
      priority = 'medium', 
      persistToStorage = true 
    } = options;

    try {
      // Set memory cache
      this.setMemoryCache(key, userId, data, { ttl, priority });

      // Set storage cache if requested
      if (persistToStorage) {
        this.setStorageCache(key, userId, data, { ttl, priority });
      }

      const duration = performance.now() - startTime;
      performanceMonitor.recordQuery('enhanced_cache_set', duration, { cached: true });
      
      logger.debug(`💾 CACHE SET: ${key} for user ${userId} (${duration.toFixed(2)}ms)`);
      return true;

    } catch (error) {
      const duration = performance.now() - startTime;
      performanceMonitor.recordQuery('enhanced_cache_set_error', duration, { 
        cached: false, 
        error: error instanceof Error ? error.message : String(error) 
      });
      
      logger.error(`Cache set error for ${key}:`, error);
      return false;
    }
  }

  /**
   * 🧹 INTELLIGENT EVICTION: Remove least important entries
   */
  private static evictLeastImportant(): void {
    if (this.memoryCache.size <= this.MAX_MEMORY_ENTRIES) return;

    const entries = Array.from(this.memoryCache.entries());
    
    // Sort by priority and access patterns
    entries.sort((a, b) => {
      const [, entryA] = a;
      const [, entryB] = b;
      
      // Priority weights
      const priorityWeight = { critical: 4, high: 3, medium: 2, low: 1 };
      const weightA = priorityWeight[entryA.priority] * entryA.accessCount;
      const weightB = priorityWeight[entryB.priority] * entryB.accessCount;
      
      // Consider recency
      const ageA = Date.now() - entryA.lastAccessed;
      const ageB = Date.now() - entryB.lastAccessed;
      
      return (weightA - ageA / 1000) - (weightB - ageB / 1000);
    });

    // Remove least important entries
    const toRemove = Math.ceil(this.MAX_MEMORY_ENTRIES * 0.2); // Remove 20%
    for (let i = 0; i < toRemove && i < entries.length; i++) {
      const [key] = entries[i];
      this.memoryCache.delete(key);
      this.stats.evictions++;
    }

    logger.debug(`🧹 CACHE EVICTION: Removed ${toRemove} entries`);
  }

  /**
   * 📊 CACHE ANALYTICS: Get performance statistics
   */
  static getStats(): CacheStats & { hitRate: number; efficiency: number } {
    const total = this.stats.hits + this.stats.misses;
    const hitRate = total > 0 ? this.stats.hits / total : 0;
    const efficiency = hitRate > 0.8 ? 'excellent' : hitRate > 0.6 ? 'good' : 'needs_improvement';

    return {
      ...this.stats,
      hitRate,
      efficiency: hitRate
    };
  }

  /**
   * 🔄 CACHE INVALIDATION: Smart invalidation strategies
   */
  static invalidate(pattern: string, userId?: string): number {
    let invalidated = 0;

    // Invalidate memory cache
    for (const [key, entry] of this.memoryCache.entries()) {
      if (key.includes(pattern) && (!userId || entry.userId === userId)) {
        this.memoryCache.delete(key);
        invalidated++;
      }
    }

    // Invalidate storage cache
    if (typeof localStorage !== 'undefined') {
      const keysToRemove: string[] = [];
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.includes(pattern) && (!userId || key.includes(userId))) {
          keysToRemove.push(key);
        }
      }
      keysToRemove.forEach(key => localStorage.removeItem(key));
      invalidated += keysToRemove.length;
    }

    logger.debug(`🔄 CACHE INVALIDATION: Removed ${invalidated} entries matching pattern: ${pattern}`);
    return invalidated;
  }

  // Private helper methods
  private static getMemoryKey(key: string, userId: string): string {
    return `${userId}:${key}`;
  }

  private static isValid<T>(entry: CacheEntry<T>): boolean {
    return Date.now() < entry.expiry;
  }

  private static setMemoryCache<T>(
    key: string, 
    userId: string, 
    data: T, 
    options: { ttl?: number; priority?: 'critical' | 'high' | 'medium' | 'low' }
  ): void {
    const { ttl = this.DEFAULT_TTL, priority = 'medium' } = options;
    
    // Evict if necessary
    if (this.memoryCache.size >= this.MAX_MEMORY_ENTRIES) {
      this.evictLeastImportant();
    }

    const entry: CacheEntry<T> = {
      data,
      timestamp: Date.now(),
      expiry: Date.now() + ttl,
      userId,
      version: this.VERSION,
      accessCount: 1,
      lastAccessed: Date.now(),
      priority
    };

    this.memoryCache.set(this.getMemoryKey(key, userId), entry);
    this.stats.entryCount++;
  }

  private static setStorageCache<T>(
    key: string, 
    userId: string, 
    data: T, 
    options: { ttl?: number; priority?: 'critical' | 'high' | 'medium' | 'low' }
  ): void {
    if (typeof localStorage === 'undefined') return;

    const { ttl = this.DEFAULT_TTL, priority = 'medium' } = options;
    const storageKey = `blissnet_enhanced_${userId}_${key}`;
    
    const entry: CacheEntry<T> = {
      data,
      timestamp: Date.now(),
      expiry: Date.now() + ttl,
      userId,
      version: this.VERSION,
      accessCount: 1,
      lastAccessed: Date.now(),
      priority
    };

    try {
      localStorage.setItem(storageKey, JSON.stringify(entry));
    } catch (error) {
      logger.warn(`Failed to set storage cache for ${key}:`, error);
    }
  }

  private static getFromStorage<T>(key: string, userId: string): T | null {
    if (typeof localStorage === 'undefined') return null;

    const storageKey = `blissnet_enhanced_${userId}_${key}`;
    
    try {
      const stored = localStorage.getItem(storageKey);
      if (!stored) return null;

      const entry: CacheEntry<T> = JSON.parse(stored);
      
      // Validate entry
      if (entry.userId !== userId || entry.version !== this.VERSION || !this.isValid(entry)) {
        localStorage.removeItem(storageKey);
        return null;
      }

      return entry.data;
    } catch (error) {
      logger.warn(`Failed to get storage cache for ${key}:`, error);
      return null;
    }
  }
}

export default EnhancedChatCache;
